import type {
  BigQueryUsageBucketFeature,
  BigQueryUsageFeatureOption,
  BigQueryUsageFeatureTotals,
  BigQueryUsageGroupTotals,
} from "@/features/bigquery-usage/api/bigquery-usage.api";

/**
 * The band a feature the service registry does not cover rolls into.
 *
 * KTD8: the mapping ships from `/features` rather than a bundled copy, so a
 * feature it does not name is a real gap in the registry — not a stale bundle —
 * and it has to stay countable. Dropping it would make the service stack sum to
 * less than the KPI strip directly above it, which reads as a rounding error and
 * is actually missing bytes.
 */
export const UNKNOWN_SERVICE = "__unknown_service__";

/** Only covered features go in the map; `service: null` is a declared gap. */
export function buildServiceByFeature(
  options: readonly BigQueryUsageFeatureOption[],
): Map<string, string> {
  const byFeature = new Map<string, string>();
  for (const option of options) {
    if (option.service != null) byFeature.set(option.feature, option.service);
  }
  return byFeature;
}

export function serviceForFeature(
  serviceByFeature: ReadonlyMap<string, string>,
  feature: string,
): string {
  return serviceByFeature.get(feature) ?? UNKNOWN_SERVICE;
}

export interface BigQueryServiceTotals extends BigQueryUsageGroupTotals {
  service: string;
  /** Which features rolled in, so a surprising row can be opened up. */
  features: string[];
}

interface ServiceAccumulator extends BigQueryServiceTotals {
  /** Duration is averaged per query, so the weighted numerator accrues here. */
  durationMsWeighted: number;
  durationQueries: number;
  /** One unpriced feature makes the service unpriced — see the rate note below. */
  isUnpriced: boolean;
}

function newAccumulator(service: string): ServiceAccumulator {
  return {
    service,
    features: [],
    queries: 0,
    bytesBilled: 0,
    bytesProcessed: 0,
    slotMs: 0,
    slotHours: 0,
    cacheHits: 0,
    cacheHitRate: null,
    avgDurationMs: null,
    costMicros: 0,
    durationMsWeighted: 0,
    durationQueries: 0,
    isUnpriced: false,
  };
}

/**
 * The window's features summed into the services that own them.
 *
 * Every feature lands in exactly one band, so the service totals equal the
 * feature totals for the same window — which is the whole point of the unknown
 * band. Ranked by bytes billed, matching the order the tables and the stack use.
 */
export function rollUpFeaturesByService(
  rows: readonly BigQueryUsageFeatureTotals[],
  options: readonly BigQueryUsageFeatureOption[],
): BigQueryServiceTotals[] {
  const serviceByFeature = buildServiceByFeature(options);
  const accumulators = new Map<string, ServiceAccumulator>();

  for (const row of rows) {
    const service = serviceForFeature(serviceByFeature, row.feature);
    const accumulator = accumulators.get(service) ?? newAccumulator(service);
    accumulators.set(service, accumulator);

    accumulator.features.push(row.feature);
    accumulator.queries += row.queries;
    accumulator.bytesBilled += row.bytesBilled;
    accumulator.bytesProcessed += row.bytesProcessed;
    accumulator.slotMs += row.slotMs;
    accumulator.slotHours += row.slotHours;
    accumulator.cacheHits += row.cacheHits;

    // The price per TB is one global setting, so a null cost is the whole
    // window being unpriced rather than this feature being free. Summing it as
    // a zero would report a priced `$0.00` for the service beside it.
    if (row.costMicros == null) accumulator.isUnpriced = true;
    else accumulator.costMicros = (accumulator.costMicros ?? 0) + row.costMicros;

    if (row.avgDurationMs != null) {
      accumulator.durationMsWeighted += row.avgDurationMs * row.queries;
      accumulator.durationQueries += row.queries;
    }
  }

  return [...accumulators.values()]
    .map(({ durationMsWeighted, durationQueries, isUnpriced, ...totals }) => ({
      ...totals,
      costMicros: isUnpriced ? null : totals.costMicros,
      // A window with no queries has no rate to take, and 0% would claim every
      // query missed the cache.
      cacheHitRate: totals.queries > 0 ? totals.cacheHits / totals.queries : null,
      // An average over features of wildly different sizes is only meaningful
      // weighted by how many queries each contributed.
      avgDurationMs: durationQueries > 0 ? durationMsWeighted / durationQueries : null,
    }))
    .sort((left, right) => right.bytesBilled - left.bytesBilled);
}

export interface BigQueryServiceBand {
  service: string;
  bytesBilled: number;
  queries: number;
}

/**
 * One bucket's feature bands re-cut by service, for the chart's service stack.
 * Same unknown-band rule as the table rollup, so a bar's height does not change
 * when the split does.
 */
export function serviceBandsForBucket(
  features: readonly BigQueryUsageBucketFeature[],
  serviceByFeature: ReadonlyMap<string, string>,
): BigQueryServiceBand[] {
  const bands = new Map<string, BigQueryServiceBand>();

  for (const entry of features) {
    const service = serviceForFeature(serviceByFeature, entry.feature);
    const band = bands.get(service) ?? { service, bytesBilled: 0, queries: 0 };
    band.bytesBilled += entry.bytesBilled;
    band.queries += entry.queries;
    bands.set(service, band);
  }

  return [...bands.values()].sort((left, right) => right.bytesBilled - left.bytesBilled);
}
